import { Alert, Box, Button, CircularProgress, MenuItem, Typography } from "@mui/material";
import { ExamCode } from "../../../../api/interfaces/preboard/ExamCode";
import { PinnacleTextField } from "../../../../components/PinnacleTextField";
import { useCallback, useState } from "react";
import { exportExams } from "../../../../api/services/preboard/exam";
import { handleApiError } from "../../../../utils/errorHandler";
import { pushNotification } from "../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";

const formatDateYYYYMMDD = (date = new Date()) =>
  date.toISOString().split('T')[0];

const sanitizeFilename = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')        // spaces → dash
    .replace(/[^a-z0-9-_]/g, ''); // remove unsafe chars

interface ExportExamDrawerContentProps {
  onSubmit: () => void;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  examCodes: ExamCode[];
}

const ExportExamDrawerContent: React.FC<ExportExamDrawerContentProps> = ({
  onSubmit,
  isSubmitting,
  setIsSubmitting,
  examCodes,
}) => {
  const [selectedExamCodeId, setSelectedExamCodeId] = useState<string>('');

  const selectedExam = examCodes.find(
    (exam) => exam._id === selectedExamCodeId
  );

  const handleExport = useCallback(async () => {
    if (!selectedExamCodeId || !selectedExam) return;

    try {
      setIsSubmitting(true);

      const response = await exportExams(selectedExamCodeId);
      const blob = response.data;

      const examIdentifier = sanitizeFilename(
        selectedExam.code ?? selectedExam.name ?? 'exam'
      );

      const filename = `exams-export-${examIdentifier}-${formatDateYYYYMMDD()}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      onSubmit();
    } catch (error) {
      handleApiError(error, {
        notify: (msg) => {
          pushNotification({
            details: msg,
            type: NotificationTypes.Error,
          });
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedExamCodeId, selectedExam, onSubmit, setIsSubmitting]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ pb: 2 }}>
        <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
          <Typography variant="subtitle2">Select which exam code you want to export.</Typography>
        </Alert>
      </Box>
      <PinnacleTextField
        select
        label="Select Exam"
        name="examCodeId"
        value={selectedExamCodeId}
        fullWidth
        disabled={isSubmitting}
        onChange={(e) => setSelectedExamCodeId(e.target.value)}
      >
        {examCodes.map((exam) => (
          <MenuItem key={exam._id} value={exam._id}>
            <Typography
              noWrap
              sx={{
                maxWidth: '400px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {exam.name ?? exam.code}
            </Typography>
          </MenuItem>
        ))}
      </PinnacleTextField>

      <Button
        variant="contained"
        color="primary"
        onClick={handleExport}
        disabled={isSubmitting || !selectedExamCodeId}
        startIcon={
          isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : null
        }
      >
        EXPORT EXAM
      </Button>
    </Box>
  );
};

export default ExportExamDrawerContent;
